import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// UI State Store
interface UIState {
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  selectedFranchise: string | null;
  notifications: Notification[];
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setSelectedFranchise: (franchise: string | null) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  markNotificationRead: (id: string, read?: boolean) => void;
  markAllNotificationsRead: () => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, _get) => ({
      sidebarCollapsed: false,
      theme: 'system',
      selectedFranchise: null,
      notifications: [],

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      setTheme: (theme) => set({ theme }),

      setSelectedFranchise: (franchise) =>
        set({ selectedFranchise: franchise }),

      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date(),
          read: false,
        };
        set((state) => ({
          notifications: [newNotification, ...state.notifications],
        }));
      },

      markNotificationRead: (id, read = true) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read } : n
          ),
        })),

      markAllNotificationsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        })),

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'fixmybrick-admin-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        selectedFranchise: state.selectedFranchise,
      }),
    }
  )
);

// Auth State Store
interface AuthState {
  user: User | null;
  loading: boolean;
  permissions: string[];
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setPermissions: (permissions: string[]) => void;
  logout: () => void;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
  franchise_ids: string[];
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  permissions: [],

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  setPermissions: (permissions) => set({ permissions }),
  logout: () => set({ user: null, permissions: [] }),
}));

// Data Cache Store for frequently accessed data
interface DataCacheState {
  franchises: Franchise[];
  services: Service[];
  locations: Location[];
  lastUpdated: Record<string, Date>;
  setFranchises: (franchises: Franchise[]) => void;
  setServices: (services: Service[]) => void;
  setLocations: (locations: Location[]) => void;
  updateTimestamp: (key: string) => void;
  isStale: (key: string, maxAge: number) => boolean;
}

interface Franchise {
  id: string;
  name: string;
  city: string;
  province: string;
  active: boolean;
}

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  active: boolean;
}

interface Location {
  id: string;
  name: string;
  address: string;
  franchise_id: string;
  active: boolean;
}

export const useDataCacheStore = create<DataCacheState>((set, get) => ({
  franchises: [],
  services: [],
  locations: [],
  lastUpdated: {},

  setFranchises: (franchises) => {
    set({ franchises });
    get().updateTimestamp('franchises');
  },

  setServices: (services) => {
    set({ services });
    get().updateTimestamp('services');
  },

  setLocations: (locations) => {
    set({ locations });
    get().updateTimestamp('locations');
  },

  updateTimestamp: (key) =>
    set((state) => ({
      lastUpdated: { ...state.lastUpdated, [key]: new Date() },
    })),

  isStale: (key, maxAge) => {
    const lastUpdate = get().lastUpdated[key];
    if (!lastUpdate) return true;
    return Date.now() - lastUpdate.getTime() > maxAge;
  },
}));
